'use client';
import TextEditor from '@/containers/TextEditor/TextEditor';
import useTextEditor from '@/containers/TextEditor/hooks/useTextEditor';
import { useFetchLinkTrackingDomains } from '@/containers/TrackedLinks/api';
import useAddTrackedLink from '@/containers/TrackedLinks/validators';
import { selectAccountDetails } from '@/lib/redux/app.selectors';
import { useAppDispatch } from '@/lib/redux/store';
import { InputContainer } from '@/shared/components/FormInputs';
import { Editor } from '@tiptap/react';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { addTrackedLink } from '../Jobs/redux/jobEditors.slice';
import { AddTrackingLinkFormValues } from '../Modals/AddTrackedLinkModal/AddTrackedLinkModal';

interface AddTrackedLinkFormProps {
  className?: string;
  postSubmit?: () => void;
  jobId?: string;
}

const AddTrackedLinkForm = ({
  className,
  postSubmit,
  jobId = '',
}: AddTrackedLinkFormProps) => {
  const dispatch = useAppDispatch();

  const [, setMessageContent] = useState<string>();
  const accountDetails = useSelector(selectAccountDetails);
  const [editor, setEditor] = useState<Editor | null>(null);
  const { editorPlainText: textEditorDestinationUrl } = useTextEditor(editor);

  const [isTouchedDestinationUrl, setIsTouchedDestinationUrl] = useState(false);

  useEffect(() => {
    // Dynamic? Only then apply this logic
    if (!formik.values.is_custom_slash_tag_link) {
      if (textEditorDestinationUrl) {
        setIsTouchedDestinationUrl(true);
      }
      formik?.setFieldValue('destination_url', textEditorDestinationUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textEditorDestinationUrl]);

  const {
    isLoading: isLoadingLinkTrackingDomains,
    data: linkTrackingDomains,
    // refetch: refetchLinkTrackingDomains,
  } = useFetchLinkTrackingDomains(
    accountDetails?.account_id,
    !!accountDetails?.account_id
  );

  // useEffect(() => {
  //   console.log({ linkTrackingDomains: linkTrackingDomains?.data });
  // }, [linkTrackingDomains]);

  const { AddTrackedLinkValidationSchema } = useAddTrackedLink();
  const formik = useFormik<AddTrackingLinkFormValues>({
    validateOnBlur: true,
    initialValues: {
      is_custom_slash_tag_link: false,
      destination_url: '',
      domain_id: '',
      domain_name: '',
      slash_tag: '',
    },
    validationSchema: AddTrackedLinkValidationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      formik.validateForm();
      try {
        setSubmitting(true);
        dispatch(
          addTrackedLink({
            jobId,
            data: {
              is_custom_slash_tag_link: values.is_custom_slash_tag_link,
              destination_url: values.destination_url,
              domain_id: values.domain_id,
              domain_name: values.domain_name,
              slash_tag: values.slash_tag,
            },
            autoAssign: true,
          })
        );
        setSubmitting(false);
        formik.resetForm();
        postSubmit?.();
      } catch (e) {
        setSubmitting(false);
        console.error(e);
      }
      return;
    },
  });

  const isLoading = formik.isSubmitting;

  return (
    <div className={classNames('', className)}>
      <form onSubmit={formik.handleSubmit} className="h-full">
        {/* Form: START */}
        <div className="form py-2">
          <div
            className={classNames('flex flex-col gap-2', {
              'disabled-container': isLoading,
            })}>
            {/* Tabs */}
            <InputContainer label="" isTouched={formik.touched.domain_name}>
              <div role="tablist" className="tabs tabs-box">
                <a
                  role="tab"
                  onClick={async () => {
                    await formik.setFieldValue('is_custom_slash_tag_link', false, true);
                    await formik.setFieldValue('slash_tag', '', true);
                  }}
                  className={classNames('tab', {
                    'tab-active !bg-base-100 !text-black-default dark:!text-white':
                      !formik.values.is_custom_slash_tag_link,
                    'text-gray-default dark:text-gray-400':
                      formik.values.is_custom_slash_tag_link,
                  })}>
                  Individually Tracked Link
                </a>
                <a
                  role="tab"
                  onClick={async () => {
                    await formik.setFieldValue('is_custom_slash_tag_link', true, true);
                  }}
                  className={classNames('tab', {
                    'tab-active !bg-base-100 !text-black-default dark:!text-white':
                      formik.values.is_custom_slash_tag_link,
                    'text-gray-default dark:text-gray-400':
                      !formik.values.is_custom_slash_tag_link,
                  })}>
                  Custom Tracked Link
                </a>
              </div>
            </InputContainer>
            {/* Domain Name */}
            <InputContainer
              label="Domain Name"
              error={formik.errors.domain_id}
              isTouched={formik.touched.domain_id}
              tooltipContent="Some info about Domains">
              <select
                disabled={isLoadingLinkTrackingDomains}
                className={classNames(
                  'focus:input-primary active:input-primary select select-bordered w-full text-sm',
                  {
                    '!cursor-wait': isLoadingLinkTrackingDomains,
                  }
                )}
                name="domain_id"
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  formik.handleChange(e);
                  const domainId = e.currentTarget.value;
                  if (domainId) {
                    formik.setFieldValue(
                      'domain_name',
                      linkTrackingDomains?.data?.find((d) => d.id === domainId)?.fullName
                    );
                  }
                }}
                value={formik.values.domain_id}>
                <option value="">
                  {isLoadingLinkTrackingDomains ? 'Loading domains...' : 'Select Domain'}
                </option>
                {linkTrackingDomains?.data?.map((domain, idx) => {
                  return (
                    <option key={idx} value={domain.id}>
                      {domain.fullName}
                    </option>
                  );
                })}
              </select>
            </InputContainer>

            {/* Slash Tag */}
            {formik.values.is_custom_slash_tag_link && (
              <InputContainer
                label="Slash Tag"
                error={formik.errors.slash_tag}
                isTouched={formik.touched.slash_tag}>
                <input
                  type="text"
                  name="slash_tag"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.slash_tag}
                  placeholder="eg: 'tag'"
                  className="focus:input-primary active:input-primary input input-bordered w-full text-sm"
                />
              </InputContainer>
            )}

            {/* Desgination URL - Static */}
            {formik.values.is_custom_slash_tag_link && (
              <InputContainer
                label="Destination URL"
                helperText="Must be fully qualified URL including http:// or https://"
                error={formik.errors.destination_url}
                isTouched={formik.touched.destination_url}>
                <input
                  type="text"
                  name="destination_url"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.destination_url}
                  placeholder="http://facebook.com"
                  className="focus:input-primary active:input-primary input input-bordered w-full text-sm"
                />
              </InputContainer>
            )}

            {/* Desgination URL - Dynamic */}
            {!formik.values.is_custom_slash_tag_link && (
              <InputContainer
                label="Destination URL"
                error={formik.errors.destination_url}
                isTouched={isTouchedDestinationUrl}
                helperText="Must be fully qualified URL including http:// or https://"
                labelClassName="!pb-0">
                <TextEditor
                  setEditor={setEditor}
                  onChange={setMessageContent}
                  tools={['attributes']}
                  isInlineToolbar={true}
                  placeholderText="https://facebook.com"
                  containerClassName={classNames(
                    'primary input-container-custom-border input input-bordered h-10 flex items-center !mb-5',
                    {
                      '!outline !outline-2 !outline-primary': editor?.isFocused,
                    }
                  )}
                  className="!mt-3 mb-2 !h-8 !min-h-7 !pb-0"
                  placeholderClassName="!top-2.5 !left-4 text-sm !text-gray-400"
                  // placeholder={<span>Enter</span>}
                  shouldAppendStopMessage={false}
                  disallowKeyboardShortcut={true}
                />
              </InputContainer>
            )}

            <button
              className="btn btn-primary mt-5 mb-3 w-full"
              type="submit"
              disabled={!formik.isValid || isLoading}>
              Create and add
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddTrackedLinkForm;
